// wishlist.js (version avec correctifs pour .wishlist-btn et .wishlist-btn-large)

class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlist = this.getWishlistFromStorage();
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateWishlistUI();
    this.updateHeaderCount();

    // Si on est sur la page favoris, charger les produits
    if (window.location.pathname === '/pages/wishlist') {
      this.loadWishlistPage();
    }
  }

  bindEvents() {
    // Écouteur global (capture) pour intercepter au plus tôt
    document.addEventListener(
      'click',
      (e) => {
        const wishlistBtn = e.target.closest('.wishlist-btn, .wishlist-btn-large');
        if (wishlistBtn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.toggleWishlist(wishlistBtn);
          return false;
        }
      },
      true
    );

    // Événements directs sur les boutons existants (petits + grands)
    this.attachDirectEvents();

    // Observer les nouveaux boutons ajoutés dynamiquement
    this.observeNewButtons();

    // Clics pour supprimer des favoris (page wishlist)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-remove-btn')) {
        e.preventDefault();
        const productId = e.target.closest('.wishlist-remove-btn').dataset.productId;
        this.removeFromWishlist(productId);
      }
    });

    // Clic "Vider la liste"
    document.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-clear-btn')) {
        e.preventDefault();
        if (confirm('Êtes-vous sûr de vouloir vider votre liste de favoris ?')) {
          this.clearWishlist();
        }
      }
    });
  }

  attachDirectEvents() {
    // Attacher des événements directs à tous les boutons wishlist existants (cards + product page)
    document.querySelectorAll('.wishlist-btn, .wishlist-btn-large').forEach((button) => {
      if (!button.hasAttribute('data-wishlist-bound')) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.toggleWishlist(button);
          return false;
        });
        button.setAttribute('data-wishlist-bound', 'true');
      }
    });
  }

  observeNewButtons() {
    // Observer les nouveaux boutons ajoutés dynamiquement (AJAX)
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                const newButtons = node.querySelectorAll
                  ? node.querySelectorAll('.wishlist-btn, .wishlist-btn-large')
                  : [];
                newButtons.forEach((button) => {
                  if (!button.hasAttribute('data-wishlist-bound')) {
                    button.addEventListener('click', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      this.toggleWishlist(button);
                      return false;
                    });
                    button.setAttribute('data-wishlist-bound', 'true');
                    // Mettre à jour l'état du bouton en fonction du storage
                    this.updateButtonState(button);
                  }
                });
              }
            });
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  updateButtonState(button) {
    const productId = button.dataset.productId;
    if (this.isInWishlist(productId)) {
      button.classList.add('active');
      if (button.dataset.productTitle) {
        button.setAttribute('aria-label', `Retirer ${button.dataset.productTitle} des favoris`);
      }
    } else {
      button.classList.remove('active');
      if (button.dataset.productTitle) {
        button.setAttribute('aria-label', `Ajouter ${button.dataset.productTitle} aux favoris`);
      }
    }
  }

  getWishlistFromStorage() {
    try {
      const wishlist = localStorage.getItem(this.storageKey);
      return wishlist ? JSON.parse(wishlist) : [];
    } catch (error) {
      console.error('Erreur lors de la lecture des favoris:', error);
      return [];
    }
  }

  saveWishlistToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des favoris:', error);
    }
  }

  toggleWishlist(button) {
    const productId = button.dataset.productId;
    const productData = {
      id: productId,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
    };

    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId);
      if (productData.title) this.showNotification(`${productData.title} retiré des favoris`);
    } else {
      this.addToWishlist(productData);
      if (productData.title) this.showNotification(`${productData.title} ajouté aux favoris`);
    }

    // MAJ immédiate du bouton cliqué (feedback instantané)
    this.updateButtonState(button);

    // Puis MAJ globale (tous les boutons, header, page wishlist)
    this.updateWishlistUI();
    this.updateHeaderCount();
  }

  addToWishlist(product) {
    if (!this.isInWishlist(product.id)) {
      this.wishlist.push(product);
      this.saveWishlistToStorage();
    }
  }

  removeFromWishlist(productId) {
    this.wishlist = this.wishlist.filter((item) => item.id !== productId);
    this.saveWishlistToStorage();

    // Si on est sur la page favoris, supprimer l'élément du DOM
    const wishlistItem = document.querySelector(`[data-wishlist-id="${productId}"]`);
    if (wishlistItem) {
      wishlistItem.remove();
      this.updateWishlistPage();
    }

    // Mettre à jour l'état des boutons correspondants (cards + product page)
    document.querySelectorAll(`.wishlist-btn[data-product-id="${productId}"], .wishlist-btn-large[data-product-id="${productId}"]`)
      .forEach((btn) => this.updateButtonState(btn));
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlistToStorage();
    this.updateWishlistUI();
    this.updateHeaderCount();

    if (window.location.pathname === '/pages/wishlist') {
      this.loadWishlistPage();
    }

    this.showNotification('Liste de favoris vidée');
  }

  isInWishlist(productId) {
    return this.wishlist.some((item) => item.id === productId);
  }

  updateWishlistUI() {
    // Mettre à jour tous les boutons favoris (cards + product page)
    document.querySelectorAll('.wishlist-btn, .wishlist-btn-large').forEach((button) => {
      this.updateButtonState(button);
    });

    // Re-attacher les événements aux nouveaux boutons
    this.attachDirectEvents();
  }

  updateHeaderCount() {
    const wishlistIcon = document.getElementById('wishlist-icon-bubble');
    if (!wishlistIcon) return;

    const countBubble = wishlistIcon.querySelector('.wishlist-count-bubble');
    if (!countBubble) return;

    const count = this.wishlist.length;

    if (count > 0) {
      countBubble.classList.remove('hidden');
      const visSpan = countBubble.querySelector('.visually-hidden');
      const numSpan = countBubble.querySelector('span[aria-hidden="true"]');
      if (numSpan) numSpan.textContent = count;
      if (visSpan) visSpan.textContent = `${count} articles dans les favoris`;
    } else {
      countBubble.classList.add('hidden');
    }
  }

  loadWishlistPage() {
    const wishlistContent = document.getElementById('wishlist-content');
    if (!wishlistContent) return;

    // Mettre à jour le compteur dans le titre
    const countElement = document.querySelector('.wishlist-count');
    if (countElement) {
      const count = this.wishlist.length;
      countElement.textContent = count > 0 ? `(${count} ${count === 1 ? 'produit' : 'produits'})` : '';
    }

    if (this.wishlist.length === 0) {
      wishlistContent.innerHTML = this.getEmptyWishlistHTML();
    } else {
      wishlistContent.innerHTML = this.getWishlistProductsHTML();
    }
  }

  updateWishlistPage() {
    // Mettre à jour le compteur
    const countElement = document.querySelector('.wishlist-count');
    if (countElement) {
      const count = this.wishlist.length;
      countElement.textContent = count > 0 ? `(${count} ${count === 1 ? 'produit' : 'produits'})` : '';
    }

    // Si plus d'articles, afficher le message vide
    if (this.wishlist.length === 0) {
      const wishlistContent = document.getElementById('wishlist-content');
      if (wishlistContent) {
        wishlistContent.innerHTML = this.getEmptyWishlistHTML();
      }
    }
  }

  getEmptyWishlistHTML() {
    return `
      <div class="wishlist-empty">
        <h2 class="wishlist-empty-title">Votre liste de favoris est vide</h2>
        <p class="wishlist-empty-text">
          Découvrez nos produits et ajoutez vos coups de cœur à votre liste de favoris en cliquant sur l'icône ♡
        </p>
        <a href="/collections/all" class="wishlist-empty-button">Découvrir nos produits</a>
      </div>
    `;
  }

  getWishlistProductsHTML() {
    return `
      <div class="wishlist-products">
        ${this.wishlist.map((product) => this.getProductHTML(product)).join('')}
      </div>
      <div class="wishlist-clear-all">
        <button class="wishlist-clear-btn" type="button">Vider la liste de favoris</button>
      </div>
    `;
  }

  getProductHTML(product) {
    return `
      <div class="wishlist-item" data-wishlist-id="${product.id}">
      <div>
       <div class="wishlist-item-image">
          <img src="${product.image}" alt="${product.title}" loading="lazy">
          <button class="wishlist-remove-btn" data-product-id="${product.id}" aria-label="Supprimer ${product.title} des favoris">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="wishlist-item-details">
          <a href="${product.url}" class="wishlist-item-title">${product.title}</a>
        </div> 
      </div>
       
        <div>
         <div class="wishlist-item-price">${this.formatPrice(product.price)}</div>
         <div class="wishlist-item-actions">
            <a href="${product.url}" class="wishlist-add-to-cart">Voir le produit</a>
         </div>
        </div>
        
      </div>
    `;
  }

  formatPrice(price) {
    if (!price) return '';

    if (typeof price === 'string' && price.includes('€')) {
      return price;
    }

    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) return price;

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(numericPrice);
  }

  showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      animation: slideInRight 0.3s ease;
    `;

    if (!document.getElementById('wishlist-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'wishlist-notification-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Initialiser le système de favoris quand le DOM est prêt
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistManager = new Wishlist();
});

// Support pour les pages qui se chargent dynamiquement
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.wishlistManager) {
      window.wishlistManager = new Wishlist();
    }
  });
} else {
  if (!window.wishlistManager) {
    window.wishlistManager = new Wishlist();
  }
}

// Initialisation avec délai pour s'assurer que tout est chargé
setTimeout(() => {
  if (!window.wishlistManager) {
    window.wishlistManager = new Wishlist();
  } else {
    // Réinitialiser les événements
    window.wishlistManager.attachDirectEvents();
    window.wishlistManager.updateWishlistUI();
  }
}, 1000);
